import { cn } from '@invent/wl-ui-kit-next';
import * as React from 'react';

import { HelpPopover } from '@/design';

/**
 * A card of the firm page (SR-423): an icon and a title, the "?" with what it shows, and an
 * action at the right — "View more in Analytics".
 */
export function Panel({
  title,
  icon: Icon,
  help,
  action,
  className,
  children
}: {
  title: string;
  icon?: React.ComponentType<{ className?: string }>;
  help?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  const id = React.useId();
  return (
    <section
      aria-labelledby={id}
      className={cn('grid content-start rounded-lg border border-border bg-card', className)}
    >
      <header className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        {Icon && <Icon className="size-4 shrink-0 text-[var(--veritant-royal-70)]" aria-hidden />}
        <h2 id={id} className="text-[0.9375rem] font-medium text-[var(--color-royal-blue)]">
          {title}
        </h2>
        {help && <HelpPopover label={`About ${title}`}>{help}</HelpPopover>}
        {action && <span className="ml-auto text-xs">{action}</span>}
      </header>
      <div className="grid gap-4 p-4 text-sm">{children}</div>
    </section>
  );
}

/** A label over a value — small caps over the figure. */
export function Figure({
  label,
  children,
  className
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  const id = React.useId();
  return (
    <div role="group" aria-labelledby={id} className={cn('grid content-start gap-0.5', className)}>
      <span id={id} className="text-[0.6875rem] tracking-wide text-muted-foreground uppercase">
        {label}
      </span>
      <div className="wrap-anywhere">{children}</div>
    </div>
  );
}

/** A pill's look — `Pills`', and a pill that links. */
export const PILL = 'rounded-full border border-border bg-white px-2.5 py-0.5 text-[0.8125rem]';

/** A row of pills. */
export function Pills({ label, values }: { label: string; values: string[] }) {
  return (
    <ul aria-label={label} className="flex flex-wrap gap-1.5">
      {values.map((value) => (
        <li key={value} className={PILL}>
          {value}
        </li>
      ))}
    </ul>
  );
}

/** A client breakdown with nothing to draw: an exempt reporting adviser reports none. */
export function NoClients({ isEra, said = 'No clients reported.' }: { isEra: boolean; said?: string }) {
  return (
    <p className="text-muted-foreground">
      {isEra ? 'Not reported by exempt reporting advisers.' : said}
    </p>
  );
}
